<template>
  <div class="formSearchWrapper">
    <label class="formLabel" :for="`tag-items-${_uid}`" v-if="showLabel">{{ label }}</label>
    <div class="formSearch">
      <template v-if="tagSelection.length > 0">
        <template v-for="tag in tagSelection">
          <span class="formSearch__Item" :key="tag.id" v-if="getTitle(tag)">
            {{ getTitle(tag) }}
            <button type="button" @click="removeTag(tag)">
              <icon weight="regular" name="times"></icon>
            </button>
          </span>
        </template>
      </template>
      <div class="formSearch__InputWrapper" v-if="multiple || (!multiple && tagSelection.length === 0)">
        <icon class="formSearch__Icon" :name="icon"></icon>
        <p class="formSearch__Loader" v-if="loading">{{ $store.getters.getStringTranslation('loading') }}...</p>
        <input
          :id="`tag-items-${_uid}`"
          class="formInput--Search"
          type="text"
          @keyup="search.length > 2 || search.length === 0 ? getTagOptions() : null"
          @click="getTagOptions()"
          @focus="focused = true"
          @blur="focused = false"
          v-model="search"
          :placeholder="
            $store.getters.getStringTranslation(placeholder.replace(/\s/g, '').toLowerCase()) || placeholder
          "
        />
      </div>
      <ul
        class="formSearch__List"
        :class="[{ 'is-Active': focused === true }, { 'formSearch__List--Top': direction === 'top' }]"
        v-if="tagOptions !== undefined"
      >
        <li class="formSearch__ListItem" v-for="tag in tagOptions" :key="tag.id">
          <span @click="pushTag(tag)">
            <strong>{{ tag.type.replace(/_/g, ' ') }}{{ tag.type && `:` }}</strong> {{ getTitle(tag) }}
          </span>
        </li>
        <li class="formSearch__ListItem" v-if="noResults === true">
          <span>{{ errorMessage }}</span>
        </li>
        <li class="formSearch__ListItem" v-else-if="tagOptions.length === 0">
          <span>{{ $store.getters.getStringTranslation('loading') }}...</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import debounce from 'tiny-debounce'
import HTTP from '@/http-common.js'

export default {
  props: {
    types: {
      type: Array,
      default: function () {
        return []
      },
    },
    countryParentTag: {
      type: Object,
      default: () => {
        return null
      },
    },
    lang: {
      type: String,
      default: 'en',
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    tags: {},
    showLabel: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Search by name',
    },
    label: {
      type: String,
      default: 'Tags',
    },
    errorMessage: {
      type: String,
      default: 'No tags found',
    },
    icon: {
      type: String,
      default: 'search',
    },
    limit: {
      default: 100,
    },
    direction: {
      default: 'bottom',
    },
  },
  name: 'SearchTag',
  data() {
    return {
      search: '',
      ids: [],
      tagSelection: [],
      tagOptions: [],
      loading: false,
      noResults: false,
      focused: false,
    }
  },
  watch: {
    ids() {
      if (this.multiple) {
        this.$emit('input', this.ids.length > 0 ? this.ids : [])
      } else {
        this.$emit('input', this.ids[0] || null)
      }
    },
    tagSelection() {
      this.$emit('onchange', this.tagSelection)
    },
    $route(to, from) {
      // When creating a campaign backend creates campaign tag that needs to be selected
      if (from.name === 'Create campaign' && to.name === 'Manage campaign') {
        this.preSelectTags()
      }
    },
    async countryParentTag(tag) {
      if (tag.parentTag.parent_id) {
        try {
          const { data } = await HTTP.get(`tag/${tag.parentTag.parent_id}`)
          tag.parentTag.parentTag = data
          if (!this.ids.includes(tag.parentTag.id)) {
            this.pushTag(tag.parentTag)
          }
        } catch (e) {
          console.log(e)
        }
      } else if (!this.ids.includes(tag.parentTag.id)) {
        this.pushTag(tag.parentTag)
      }
    },
  },
  methods: {
    getTitle(tag) {
      if (tag && tag.l10n.find((obj) => obj.lang === this.lang))
        return tag.l10n.find((obj) => obj.lang === this.lang).name
      return tag.l10n[0].name
    },

    // Tag actions
    getTagOptions: debounce(function () {
      this.loading = true

      const searchParams = {
        search_query: this.search,
        order_by: 'alphabetical',
        order_dir: 'asc',
        types: this.types,
        lang: this.lang,
        parent_tag: true,
      }

      if (this.search.length === 0 && this.limit) searchParams.limit = this.limit

      HTTP.post('search/tag', searchParams).then((response) => {
        this.loading = false
        this.tagOptions = response.data.tags.filter((a) => this.ids.indexOf(a.id) === -1) // Don't show options that are already selected

        if (this.tagOptions.length === 0) {
          this.noResults = true
        } else {
          this.noResults = false
        }
      })
    }, 500),

    pushTag(tag) {
      // quick fix for orgs
      if (this.$route.name !== 'Edit organisation') {
        if (tag.type === 'country') {
          this.handleCountryParent(tag)
        } else {
          if (
            !this.isParent(tag) &&
            !this.ids.includes(tag.parent_id) &&
            this.$route.name !== 'Edit tag' &&
            this.$route.name !== 'Add tag'
          ) {
            this.tagSelection.push(tag.parentTag)
            this.ids.push(tag.parent_id)
          }
        }
      }
      this.ids.push(tag.id)
      this.tagSelection.push(tag)

      const index = this.tagOptions.findIndex((x) => x.id === tag.id)
      this.tagOptions.splice(index, 1)
      this.search = ''
    },

    isParent(tag) {
      return !tag.parent_id
    },

    removeTag(tag) {
      this.tagOptions.push(tag)
      // Default is to remove child tags when you remove parent but region tags & countries stays
      if (this.isParent(tag) && tag.type !== 'region') {
        this.tagSelection = this.tagSelection.filter((t) => t.parent_id !== tag.id)
        this.ids = this.tagSelection.map((t) => t.id)
      }
      const index = this.tagSelection.findIndex((x) => x.id === tag.id)
      this.ids.splice(index, 1)
      this.tagSelection.splice(index, 1)
    },

    handleCountryParent(tag) {
      this.$emit('handleParentTag', tag)
    },

    preselectTags() {
      if (typeof this.tags === 'object' && !Array.isArray(this.tags) && this.tags !== null) {
        this.tagSelection = [this.tags]
        this.ids = [this.tags.id]
      } else if (this.tags && this.tags.length > 0 && typeof this.tags[0] === 'object') {
        this.tagSelection = this.tags
        this.ids = this.tags.map((tag) => tag.id)
      } else if (this.tags && this.tags.length > 0 && typeof this.tags[0] === 'number') {
        this.ids = this.tags
        HTTP.post(`search/tag`, { ids: this.tags }).then((response) => {
          response.data.tags.forEach((tag) => {
            this.tagSelection.push(tag)
          })
        })
      }
    },
  },

  created() {
    this.preselectTags()
  },
}
</script>
